import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { OrcaTaskGate, OrcaTaskPhase } from "./OrcaTask.ts";

/**
 * Typed event vocabulary for the cross-task-queryable `orca_task_events`
 * log (INF-752). This COMPLEMENTS the per-task jsonb `history` array on
 * `orca_tasks` (INF-521): the history stays the forensic per-row timeline,
 * while this table is the queryable cross-task index the stalled-pipeline
 * alert and failure-mode summary (INF-753) read.
 *
 * Vocabulary rule: reuse the {@link OrcaTaskHistoryEvent} `kind` names where
 * one already fits (`phase_transition`, `workflow_dispatch`, `error`) so
 * consumers query a single stable name across both stores; add a new literal
 * ONLY where no history kind fits:
 *
 * - `gate_infra_failure` — a gate workflow failed for an infrastructure
 *   reason rather than a code verdict. Defined here as the vocabulary for
 *   INF-751's classifier, which wires into this emit when it lands; this
 *   issue only defines the literal (cross-project blocks edge is ignored).
 * - `dispatch_deferred` — a task was parked pre-spawn (blocked upstream, or
 *   an environmental-retry backoff) instead of dispatching a builder.
 * - `rebase_tooling_error` — a server-side rebase / merge tooling step failed.
 * - `suspect_marked` — a runner/task was flagged suspect by the watchdog.
 * - `manual_intervention` — an operator mutated the task via an admin RPC
 *   (kill / reset / tombstone).
 * - `builder_lost` (OL-129) — the liveness rule declared a builder / fixer
 *   container lost. The structured evidence (last heartbeat, snapshot moment,
 *   container name, detecting path) lives in the paired
 *   {@link BuilderLostHistoryEvent} history entry; `reason` carries the short
 *   operator-readable line, since this table has no payload column.
 * - `duplicate_killed` (OL-129) — the kill helper force-removed a container that
 *   was still present (paired with {@link DuplicateKilledHistoryEvent}).
 * - `pr_adopted` (OL-129) — a recovery path adopted an already-open PR instead
 *   of re-spawning (paired with {@link PrAdoptedHistoryEvent}).
 * - `recovery_applied` (OL-159) — the recovery playbook ran one named recovery
 *   against a task. `reason` carries the recovery NAME (not a sentence), which
 *   is what makes {@link OrcaTaskEventsAccessor.summarizeByReasonSince} a
 *   "recoveries by name" rollup. The forensic detail lives in the paired
 *   {@link RecoveryHistoryEvent} history entry.
 * - `recovery_escalated` (OL-159) — a recovery repeated past its per-task
 *   ceiling and was escalated instead of retried. Counted per task per window
 *   by {@link OrcaTaskEventsAccessor.countByTaskAndTypeSince}.
 * - `recovery_skipped_budget` (OL-159) — a recovery was declined because the
 *   task had already spent its recovery budget for the window.
 * - `fix_loop_capped` (OL-141) — the fix loop hit its cap for a repeating
 *   verdict and stopped dispatching fixers. Defined here as the vocabulary for
 *   OL-145's cap, which writes it when it lands.
 * - `abnormal_exit` (OL-141) — a builder / fixer container exited abnormally.
 *   Defined here as the vocabulary for OL-148's classifier.
 *
 * The `event_type` column is a plain `varchar().$type<OrcaTaskEventType>()`
 * (NOT a PG enum) so a future literal never trips the enum-value-isolation
 * two-deploy rule (see `packages/db/CLAUDE.md` "Enum Value Isolation").
 *
 * @since 1.0.0
 */
export const OrcaTaskEventType = Schema.Literal(
  "phase_transition",
  "workflow_dispatch",
  "error",
  "gate_infra_failure",
  "dispatch_deferred",
  "rebase_tooling_error",
  "suspect_marked",
  "manual_intervention",
  "builder_lost",
  "duplicate_killed",
  "pr_adopted",
  "fix_loop_capped",
  "abnormal_exit",
  "recovery_applied",
  "recovery_escalated",
  "recovery_skipped_budget",
);
export type OrcaTaskEventType = Schema.Schema.Type<typeof OrcaTaskEventType>;

/**
 * Structured evidence for a gate-driven event (OL-141) — the machine-readable
 * half of what `reason` says in prose, so "which jobs / criteria / files kept
 * failing" becomes a group-by instead of a regex over free text.
 *
 * Every field is optional because each gate contributes a different slice: a
 * `ci` dispatch carries job names, a `completeness` dispatch carries unmet
 * criteria, a `review` dispatch carries issues, and a `merge` dispatch carries
 * conflicted files. `bucket` is the coarse failure class OL-142 computes.
 *
 * @since 1.0.0
 */
export const OrcaTaskEventDetail = Schema.Struct({
  failedRequiredJobs: Schema.optional(Schema.Array(Schema.String)),
  failedOtherJobs: Schema.optional(Schema.Array(Schema.String)),
  unmetCriteria: Schema.optional(Schema.Array(Schema.String)),
  issues: Schema.optional(Schema.Array(Schema.String)),
  conflictedFiles: Schema.optional(Schema.Array(Schema.String)),
  bucket: Schema.optional(Schema.String),
});
export type OrcaTaskEventDetail = Schema.Schema.Type<typeof OrcaTaskEventDetail>;

/**
 * OrcaTaskEvent model — one row per meaningful event on an orca task, stored
 * in a queryable cross-task table. `fromPhase` / `toPhase` are populated for
 * `phase_transition` (and any other event with a phase context) and are null
 * for events that carry no phase (e.g. a `manual_intervention` kill). `reason`
 * is a free-text operator-readable explanation.
 *
 * `gate` / `verdictKey` / `detail` (OL-141) are the structured half of a
 * fixer-dispatch record: which gate failed, a stable hash of that gate's
 * verdict (OL-142 computes it), and the {@link OrcaTaskEventDetail} evidence.
 * All three are null on every event written before OL-143 wires the writes,
 * and on every event that carries no gate context.
 *
 * @since 1.0.0
 */
export class OrcaTaskEvent extends Model.Class<OrcaTaskEvent>("OrcaTaskEvent")({
  id: Model.Generated(Schema.String),
  taskId: Schema.String,
  linearIssueIdentifier: Schema.String,
  eventType: OrcaTaskEventType,
  fromPhase: Schema.NullOr(OrcaTaskPhase),
  toPhase: Schema.NullOr(OrcaTaskPhase),
  reason: Schema.NullOr(Schema.String),
  gate: Schema.NullOr(OrcaTaskGate),
  verdictKey: Schema.NullOr(Schema.String),
  detail: Schema.NullOr(OrcaTaskEventDetail),
  createdAt: Model.DateTimeInsertFromDate,
}) {}
